import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import TheoryExamplesRunner from "../../components/TheoryExamplesRunner";

export default function TriangleAreaCosinePreparationTheory() {
  const intro = (
    <div className="space-y-3">
      <p>
        Pole trójkąta najłatwiej liczymy z długości podstawy i wysokości
        opuszczonej na tę podstawę:
      </p>
      <BlockMath math={String.raw`P=\frac{a\cdot h_a}{2}`} />
      <p>
        Jeżeli znamy dwa boki i kąt między nimi, korzystamy ze wzoru z sinusem:
      </p>
      <BlockMath math={String.raw`P=\frac12 ab\sin\gamma`} />
      <p>
        Gdy znamy dwa boki i kąt między nimi, a szukamy trzeciego boku,
        stosujemy <strong>twierdzenie cosinusów</strong>:
      </p>
      <BlockMath math={String.raw`c^2=a^2+b^2-2ab\cos\gamma`} />
      <p>
        W zadaniu o polu wybieramy wzór pasujący do podanych danych. W zadaniu
        z twierdzenia cosinusów najpierw obliczamy <InlineMath math="c^2" />,
        a dopiero potem długość boku <InlineMath math="c" />.
      </p>
    </div>
  );

  const examples = [
    {
      label: "Przykład 1",
      summary: "Pole z podstawy i wysokości",
      description: (
        <span>
          Podstawa ma długość <InlineMath math="8" />, a wysokość opuszczona na
          tę podstawę — <InlineMath math="5" />.
        </span>
      ),
      content: <BlockMath math={String.raw`P=\frac{8\cdot5}{2}=20`} />,
    },
    {
      label: "Przykład 2",
      summary: "Pole z dwóch boków i kąta",
      description: (
        <span>
          Dwa boki mają długości <InlineMath math="4" /> i <InlineMath math="6" />,
          a kąt między nimi ma <InlineMath math={String.raw`60^\circ`} />.
        </span>
      ),
      content: (
        <div>
          <BlockMath math={String.raw`P=\frac12\cdot4\cdot6\cdot\sin60^\circ`} />
          <BlockMath math={String.raw`P=12\cdot\frac{\sqrt3}{2}=6\sqrt3`} />
        </div>
      ),
    },
    {
      label: "Przykład 3",
      summary: "Twierdzenie cosinusów dla kąta 60°",
      description: (
        <span>
          Dwa boki mają długości <InlineMath math="5" /> i <InlineMath math="7" />.
          Obliczamy trzeci bok.
        </span>
      ),
      content: (
        <div>
          <BlockMath math={String.raw`c^2=5^2+7^2-2\cdot5\cdot7\cdot\cos60^\circ`} />
          <BlockMath math={String.raw`c^2=25+49-70\cdot\frac12=39`} />
          <BlockMath math={String.raw`c=\sqrt{39}\approx6{,}24`} />
        </div>
      ),
    },
    {
      label: "Przykład 4",
      summary: "Twierdzenie cosinusów z podaną wartością cosinusa",
      description: (
        <span>
          Dane są boki <InlineMath math="|AB|=5" /> i <InlineMath math="|AC|=2" />
          oraz <InlineMath math={String.raw`\cos|\angle BAC|=\frac35`} />.
        </span>
      ),
      content: (
        <div>
          <BlockMath math={String.raw`|BC|^2=5^2+2^2-2\cdot5\cdot2\cdot\frac35`} />
          <BlockMath math={String.raw`|BC|^2=25+4-12=17`} />
          <BlockMath math={String.raw`|BC|=\sqrt{17}`} />
        </div>
      ),
    },
  ];

  return <TheoryExamplesRunner intro={intro} examples={examples} />;
}
